import { useMemo } from "react"

import { api } from "~/utils/api"
import useShowHelix from "~/hooks/useShowHelix"
import { type PlanningLevel } from "~/utils/demandPlanningLevel"

/** ONE planned thing: a collection group at level `group`, otherwise a single collection. */
export type PlanningUnit = {
  level: PlanningLevel
  /** Absent until something is authored. */
  workspaceId: number | null
  name: string
}

/** `null` answers true: a scope that cannot yet name its unit must not withhold a publish button. */
export const authorableAt = (unit: PlanningUnit | null, isGroupRoute: boolean): boolean =>
  unit == null || unit.level === (isGroupRoute ? "group" : "collection")

const EMPTY: ReadonlyMap<number, PlanningUnit> = new Map()

/**
 * Gated on the demand-tree flag: Collection Management reaches this for every customer,
 * including orgs without the module.
 */
export const usePlanningUnits = (): ReadonlyMap<number, PlanningUnit> => {
  const showHelix = useShowHelix()
  const { data } = api.demand2.getPlanningUnits.useQuery(undefined, { enabled: showHelix })
  return useMemo(
    () =>
      data == null
        ? EMPTY
        : new Map(
            data.units.map((u) => [
              u.collectionId,
              { level: u.level, workspaceId: u.workspaceId, name: u.name },
            ]),
          ),
    [data],
  )
}
